import React from 'react';
import { Badge, Button, Card, Form, Spinner, Table } from 'react-bootstrap';
import { format } from 'date-fns';
import PaginationComponent from '../PaginationComponent';
import {
  CATEGORY_LABELS,
  PAYMENT_METHOD_LABELS,
  VOUCHER_STATUS_COLORS,
  VOUCHER_STATUS_LABELS,
} from '../../utils/cashierConstants';

/**
 * #235631 — sếp: "giao diện phiếu chi đang rất khó xem, cải thiện lại cho dễ theo dõi
 * những cái cần thiết".
 *
 * Nguyên tắc trình bày: người xem cần trả lời 5 câu trong 1 lần liếc — chi bao nhiêu,
 * chi cho cái gì, ai đề nghị, ca/chi nhánh nào, đang chờ ai duyệt. Vì vậy:
 *  - Số tiền canh phải, đậm, cỡ lớn hơn, chữ số đều bề ngang (tabular-nums) để so sánh
 *    được theo cột thay vì phải đọc từng số.
 *  - "Nội dung chi" (description) và "người đề nghị/duyệt" đưa THẲNG vào bảng — trước
 *    đây phải mở modal từng phiếu mới thấy.
 *  - Cột "Chi nhánh" riêng bị bỏ (nội dung lặp y hệt trên mọi dòng vì trang đã lọc theo
 *    chi nhánh) — dữ liệu KHÔNG mất, chuyển thành dòng phụ dưới số phiếu.
 *  - Ngày hiển thị là NGÀY CHI (voucher_date) chứ không phải ngày tạo bản ghi.
 *  - Dưới md: bỏ hẳn bảng, đổi sang danh sách thẻ để không tràn ngang trên điện thoại.
 */

const formatCurrency = (value) =>
  new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 }).format(
    Number(value || 0),
  );

const formatDate = (value) => {
  if (!value) return '-';
  try { return format(new Date(value), 'dd/MM/yyyy'); } catch { return value; }
};

const formatDateTime = (value) => {
  if (!value) return '-';
  try { return format(new Date(value), 'dd/MM/yyyy HH:mm'); } catch { return value; }
};

const AMOUNT_STYLE = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };

// Nội dung chi dài (vd phiếu thanh toán trước cho đơn NCC) bị cắt còn 2 dòng để mọi
// dòng cao đều nhau; xem đủ bằng tooltip hoặc mở chi tiết phiếu.
const CLAMP_2_LINES = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
};

const StatusBadge = ({ status }) => (
  <Badge bg={VOUCHER_STATUS_COLORS[status] || 'secondary'} className="text-nowrap">
    {VOUCHER_STATUS_LABELS[status] || status}
  </Badge>
);

/** Ai đang giữ phiếu: chờ duyệt thì nêu người đề nghị, đã xử lý thì nêu người duyệt/huỷ. */
const PeopleCell = ({ voucher }) => (
  <>
    <div className="small">
      <span className="text-muted">Đề nghị: </span>
      {voucher.createdByName || '-'}
    </div>
    {voucher.status === 'CANCELLED' ? (
      <div className="small">
        <span className="text-muted">Huỷ: </span>
        {voucher.cancelledByName || '-'}
        {voucher.cancelledAt && (
          <span className="text-muted"> · {formatDateTime(voucher.cancelledAt)}</span>
        )}
      </div>
    ) : (
      <div className="small">
        <span className="text-muted">
          {voucher.status === 'REJECTED' ? 'Từ chối: ' : 'Duyệt: '}
        </span>
        {voucher.status === 'PENDING'
          ? <span className="text-warning">đang chờ duyệt</span>
          : (voucher.approvedByName || '-')}
      </div>
    )}
  </>
);

const Actions = ({ voucher, onApprove, onReject, onDelete, onCancel, onViewDetail, canCancel, className }) => (
  <div className={`d-flex gap-2 flex-wrap ${className || ''}`}>
    {voucher.status === 'PENDING' && (
      <>
        <Button size="sm" variant="outline-success" onClick={() => onApprove(voucher)}>
          Duyệt
        </Button>
        <Button size="sm" variant="outline-warning" onClick={() => onReject(voucher)}>
          Từ chối
        </Button>
      </>
    )}
    {/* #164550 — chỉ ADMIN huỷ được phiếu ĐÃ DUYỆT (AC5/AC15). */}
    {voucher.status === 'APPROVED' && canCancel && (
      <Button size="sm" variant="outline-danger" onClick={() => onCancel(voucher)}>
        Huỷ phiếu
      </Button>
    )}
    {['PENDING', 'REJECTED'].includes(voucher.status) && (
      <Button size="sm" variant="outline-danger" onClick={() => onDelete(voucher)}>
        Xóa
      </Button>
    )}
    <Button size="sm" variant="outline-secondary" onClick={() => onViewDetail(voucher)}>
      Chi tiết
    </Button>
  </div>
);

const VoucherList = ({
  vouchers,
  loading,
  statusFilter,
  onStatusFilterChange,
  search,
  onSearchChange,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  branchLabel,
  filteredAmount,
  page,
  limit,
  totalItems,
  onPageChange,
  onApprove,
  onReject,
  onDelete,
  onCancel,
  onViewDetail,
  canCancel,
}) => (
  <Card>
    <Card.Header>
      <div className="d-flex flex-wrap justify-content-between gap-2 align-items-start mb-2">
        <div>
          <h5 className="mb-1">Danh sách phiếu chi</h5>
          <div className="small text-muted">
            {totalItems != null && <>Tổng: <strong>{totalItems}</strong> phiếu</>}
            {filteredAmount != null && (
              <>
                {' · '}Tổng tiền: <strong style={AMOUNT_STYLE}>{formatCurrency(filteredAmount)}</strong>
              </>
            )}
          </div>
        </div>
        <Badge bg="light" text="dark" className="border">
          Chi nhánh: {branchLabel || 'Tất cả chi nhánh'}
        </Badge>
      </div>

      {/* Bộ lọc của RIÊNG phiếu chi — trước đây khoảng thời gian nằm trong thẻ
          "Giám sát ca thu ngân" ở trên nên không ai biết nó áp cho danh sách này. */}
      <div className="d-flex flex-wrap gap-2 align-items-end">
        <div>
          <Form.Label className="small text-muted mb-1">Từ ngày chi</Form.Label>
          <Form.Control
            type="date"
            size="sm"
            value={dateFrom || ''}
            onChange={(e) => onDateFromChange(e.target.value)}
            style={{ minWidth: 145 }}
          />
        </div>
        <div>
          <Form.Label className="small text-muted mb-1">Đến ngày chi</Form.Label>
          <Form.Control
            type="date"
            size="sm"
            value={dateTo || ''}
            onChange={(e) => onDateToChange(e.target.value)}
            style={{ minWidth: 145 }}
          />
        </div>
        <div>
          <Form.Label className="small text-muted mb-1">Trạng thái</Form.Label>
          <Form.Select
            size="sm"
            value={statusFilter}
            onChange={(e) => onStatusFilterChange(e.target.value)}
            style={{ minWidth: 130 }}
          >
            <option value="PENDING">Chờ duyệt</option>
            <option value="APPROVED">Đã duyệt</option>
            <option value="REJECTED">Từ chối</option>
            <option value="CANCELLED">Đã huỷ</option>
            <option value="">Tất cả</option>
          </Form.Select>
        </div>
        <div className="flex-grow-1" style={{ minWidth: 180 }}>
          <Form.Label className="small text-muted mb-1">Tìm kiếm</Form.Label>
          <Form.Control
            size="sm"
            placeholder="Nội dung chi / số phiếu / người nhận..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>
      </div>
    </Card.Header>

    <Card.Body className="p-0">
      {loading ? (
        <div className="text-center py-4">
          <Spinner size="sm" animation="border" /> Đang tải phiếu chi...
        </div>
      ) : vouchers.length === 0 ? (
        <div className="text-center text-muted py-4">
          {search || statusFilter
            ? 'Không tìm thấy phiếu chi phù hợp với bộ lọc.'
            : 'Chưa có phiếu chi nào trong khoảng thời gian này.'}
        </div>
      ) : (
        <>
          {/* ─── Máy tính / tablet: bảng ─────────────────────────────────────── */}
          <div className="d-none d-md-block">
            <Table hover className="mb-0 align-middle">
              <thead>
                <tr>
                  <th style={{ width: '17%' }}>Phiếu</th>
                  <th>Nội dung chi</th>
                  <th style={{ width: '17%' }}>Người đề nghị / duyệt</th>
                  <th className="text-end" style={{ width: '14%' }}>Số tiền</th>
                  <th style={{ width: '10%' }}>Trạng thái</th>
                  <th className="text-end" style={{ width: '16%' }}>Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {vouchers.map((voucher) => (
                  <tr key={voucher.id}>
                    <td>
                      <Button
                        variant="link"
                        className="p-0 fw-semibold text-start text-nowrap"
                        onClick={() => onViewDetail(voucher)}
                      >
                        {voucher.voucherNumber || voucher.id}
                      </Button>
                      {/* #110729 — phiếu chi LÙI CA (không trừ két). */}
                      {voucher.isBackdated && (
                        <Badge bg="info" text="dark" className="ms-1 align-middle">
                          LÙI CA
                        </Badge>
                      )}
                      <div
                        className="small text-muted"
                        title={`Ngày chi. Phiếu được tạo lúc ${formatDateTime(voucher.createdAt)}`}
                      >
                        {formatDate(voucher.voucherDate || voucher.createdAt)}
                      </div>
                      <div className="small text-muted text-truncate" title={voucher.branchName}>
                        {voucher.branchName}
                      </div>
                    </td>
                    <td>
                      <div className="fw-semibold" style={CLAMP_2_LINES} title={voucher.description}>
                        {voucher.description || <span className="text-muted fst-italic">(không có mô tả)</span>}
                      </div>
                      <div className="small text-muted">
                        <Badge bg="light" text="dark" className="border me-1">
                          {CATEGORY_LABELS[voucher.category] || voucher.category}
                        </Badge>
                        {PAYMENT_METHOD_LABELS[voucher.paymentMethod] || voucher.paymentMethod}
                        {voucher.recipientName && voucher.recipientName !== 'N/A' && (
                          <> · Người nhận: {voucher.recipientName}</>
                        )}
                        {voucher.shiftId && <> · Có gắn ca</>}
                      </div>
                    </td>
                    <td><PeopleCell voucher={voucher} /></td>
                    <td className="text-end fw-bold fs-6" style={AMOUNT_STYLE}>
                      {formatCurrency(voucher.amount)}
                    </td>
                    <td><StatusBadge status={voucher.status} /></td>
                    <td>
                      <Actions
                        voucher={voucher}
                        onApprove={onApprove}
                        onReject={onReject}
                        onDelete={onDelete}
                        onCancel={onCancel}
                        onViewDetail={onViewDetail}
                        canCancel={canCancel}
                        className="justify-content-end"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* ─── Điện thoại: danh sách thẻ, không tràn ngang ─────────────────── */}
          <div className="d-md-none">
            {vouchers.map((voucher) => (
              <div key={voucher.id} className="border-bottom p-3">
                <div className="d-flex justify-content-between align-items-start gap-2">
                  <Button
                    variant="link"
                    className="p-0 fw-semibold text-start"
                    onClick={() => onViewDetail(voucher)}
                  >
                    {voucher.voucherNumber || voucher.id}
                  </Button>
                  <StatusBadge status={voucher.status} />
                </div>
                {voucher.isBackdated && (
                  <Badge bg="info" text="dark" className="mt-1">LÙI CA (không trừ két)</Badge>
                )}
                <div className="d-flex justify-content-between align-items-end gap-2 mt-1">
                  <div className="small text-muted">
                    {formatDate(voucher.voucherDate || voucher.createdAt)} · {voucher.branchName}
                  </div>
                  <div className="fw-bold fs-5" style={AMOUNT_STYLE}>
                    {formatCurrency(voucher.amount)}
                  </div>
                </div>
                <div className="mt-2 fw-semibold" style={{ whiteSpace: 'pre-wrap' }}>
                  {voucher.description || <span className="text-muted fst-italic">(không có mô tả)</span>}
                </div>
                <div className="small text-muted mt-1">
                  <Badge bg="light" text="dark" className="border me-1">
                    {CATEGORY_LABELS[voucher.category] || voucher.category}
                  </Badge>
                  {PAYMENT_METHOD_LABELS[voucher.paymentMethod] || voucher.paymentMethod}
                  {voucher.recipientName && voucher.recipientName !== 'N/A' && (
                    <> · Người nhận: {voucher.recipientName}</>
                  )}
                </div>
                <div className="mt-1"><PeopleCell voucher={voucher} /></div>
                <div className="small text-muted">Tạo lúc {formatDateTime(voucher.createdAt)}</div>
                <Actions
                  voucher={voucher}
                  onApprove={onApprove}
                  onReject={onReject}
                  onDelete={onDelete}
                  onCancel={onCancel}
                  onViewDetail={onViewDetail}
                  canCancel={canCancel}
                  className="mt-2"
                />
              </div>
            ))}
          </div>
        </>
      )}
    </Card.Body>

    {totalItems > limit && (
      <Card.Footer className="bg-white">
        <PaginationComponent
          totalItems={totalItems}
          itemsPerPage={limit}
          currentPage={page}
          onPageChange={onPageChange}
          loading={loading}
        />
      </Card.Footer>
    )}
  </Card>
);

export default VoucherList;
